import EditIcon from '@mui/icons-material/Edit'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { FeatherIcon } from 'components/Icons'
import {
  Article,
  ARTICLE_TYPE,
  ContentItem,
  FEED_TYPE_FACEBOOK,
  FEED_TYPE_INSTAGRAM,
  FEED_TYPE_KEYWORD,
  PHOTO_TYPE
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { STOCK_PHOTO_TYPE } from 'interfaces/Content/StockContentTypes'
import * as React from 'react'
import { memo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { Observable } from 'rxjs'
import {
  checkFeatureAvailability,
  contentTypeFeatureMap,
  feedTypeFeatureMap,
  getContentAgeFeature
} from 'services/product'
import { message } from 'services/snackbar'
import { openComposerContentDialog } from 'services/ui'
import { FEATURE_FIND_FAVORITES } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import CardComposeButton from './CardComposeButton'
import styles from './ContentCardActions.pcss'
import FavButton from './FavButton'

export type ContentCardAction = 'favorite' | 'create-post' | 'select' | 'open-studio' | 'ai'

const Clipboard = require('clipboard')

interface ContentCardActionsProps {
  content: ContentItem
  isFavorite: boolean
  isFavButtonVisible: boolean
  favCount: number
  className: string
  isStock?: boolean
  isBlocked?: boolean
  // EXPL: Handed to the compose button's positioned wrapper so the card can write the measured
  // "clear the footer" offset onto it without a re-render. See ContentCard's footer effect.
  composeBtnRef?: React.Ref<HTMLSpanElement>
  skipFeatureChecks?: boolean
  studioButtonIcon?: React.ReactNode
  actions?: ContentCardAction[]
  createPostLabel?: React.ReactNode
  isContentBlocked: (aiWriter: boolean) => void | boolean
  onCreatePost: (skipAddToComposer?: boolean) => boolean
  onAddFavorite: () => Observable<any>
  onRemoveFavorite: () => Observable<any>
}

export function ContentCardActions(props: ContentCardActionsProps) {
  const { content, isContentBlocked, skipFeatureChecks } = props
  const navigate = useNavigate()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const intl = useIntl()
  const clipboard = React.useRef(null)
  const getClipboardText = () => content.status?.trim() || ''

  const setRef = (element: HTMLElement | null) => {
    if (element) {
      if (clipboard.current) {
        (clipboard.current as any).destroy()
      }

      clipboard.current = new Clipboard(
        element,
        {
          text: getClipboardText,
          container: element
        }
      )
      ; (clipboard.current as any).on('error', (e: any) => {
        console.log('Clipboard error', e)
      })
    }
  }

  const feedType = content.feed.type
  const isPhoto = content.type === PHOTO_TYPE || content.type === STOCK_PHOTO_TYPE
  const isArticle = content.type === ARTICLE_TYPE
  const isText = content.type === TEXT_TYPE

  const checkIsContentBlocked = React.useCallback((aiWriter: boolean) => {
    if (skipFeatureChecks) {
      return false
    }

    // EXPL: `checkFeatureAvailability` is a thunk - calling it bare returns the thunk function,
    // which is always truthy, so every gate below used to be dead code. It has to go through
    // `dispatch`, which is also what raises the upgrade prompt for the blocked feature. A feature
    // the maps have no key for gates nothing: the thunk reports an undefined feature as
    // unavailable, which would block the content under an undefined feature name.
    const isBlockedBy = (feature?: string | null) => Boolean(feature) && !dispatch(checkFeatureAvailability(feature as string))

    // FB feeds are accessible for all plans
    const shouldCheckFeed = ![FEED_TYPE_FACEBOOK, FEED_TYPE_INSTAGRAM, FEED_TYPE_KEYWORD].includes(feedType)

    // Skip feed type check for Stock content
    if (!content.isStock && shouldCheckFeed && isBlockedBy(feedTypeFeatureMap[feedType])) {
      return true
    }

    if (isBlockedBy(contentTypeFeatureMap[content.type])) {
      return true
    }

    if (!content.isStock && isBlockedBy(getContentAgeFeature(content.createdAt))) {
      return true
    }

    if (content.isFavorite && isBlockedBy(FEATURE_FIND_FAVORITES)) {
      return true
    }

    const isBlockedFromParent = isContentBlocked(aiWriter)

    if (isBlockedFromParent) {
      return true
    }
    return false
  }, [
    dispatch,
    feedType,
    skipFeatureChecks,
    content.type,
    isContentBlocked,
    content.isStock,
    content.isFavorite,
    content.createdAt
  ])

  const onCreatePost = (e: React.MouseEvent) => {
    e.stopPropagation()
    goToComposer()
  }

  const goToComposer = (aiWriter?: boolean) => {
    if (checkIsContentBlocked(Boolean(aiWriter))) {
      return
    }

    if (content.type === TEXT_TYPE && !aiWriter) {
      dispatch(message(intl.formatMessage({ id: 'notifications.status-copied' }), 'info'))
    }

    const disableOpenComposer = props.onCreatePost(aiWriter) || location.pathname.startsWith('/composer')
    if (!disableOpenComposer) {
      if (aiWriter) {
        const path = location.pathname === '/composer' ? '/composer/ai' : location.pathname + `?view=ai`
        navigate(
          path,
          {
            state: {
              text: isArticle ? (content as Article).sourceLink : isText ? content.status || '' : undefined,
              imageUrl: isPhoto ? content.imageUrl : undefined,
              opener: location.pathname,
              background: location
            }
          })
      } else {
        navigate(
          '/composer',
          {
            state: {
              floating: true,
              modal: 'compose',
              opener: location.pathname + location.search,
              background: location
            }
          })
      }
    }
  }

  const openStudio = () => {
    if (checkIsContentBlocked(false)) {
      return
    }
    dispatch(openComposerContentDialog({ tab: 'edit', imageUrl: content.imageUrl }))
  }

  const onEdit = () => {
    openStudio()
  }


  const showStudioButton = !props.actions || props.actions.includes('open-studio')
  const isCreatePostPrimary = Boolean(props.studioButtonIcon)

  // EXPL: Default browse cards get the unified layout (bottom "Create post" + "..." menu).
  // Picker contexts (studio grid, composer panels) pass `actions`/`studioButtonIcon` and keep the legacy column.
  const useUnifiedLayout = !props.actions && !props.studioButtonIcon

  if (useUnifiedLayout) {
    // EXPL: When the upgrade overlay is shown it owns the card surface (incl. its own fav button) — hide ours
    if (props.isBlocked) {
      return null
    }

    return (
      <React.Fragment>
        <div
          className={`${styles['actions-row']} ${props.className}`}
          data-testid="content-card-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {content.feed.id && props.isFavButtonVisible && (
            <FavButton
              isFavorite={props.isFavorite}
              count={props.favCount}
              hover={false}
              size="small"
              tooltipPlacement="top"
              className={styles['fav-btn']}
              onFavorite={props.onAddFavorite}
              onUnfavorite={props.onRemoveFavorite}
            />
          )}


          {isPhoto && showStudioButton && (
            <Tooltip title={<FormattedMessage id="content-card.actions.edit-image" />}>
              <IconButton
                key="studio"
                size="small"
                className={styles.btnEdit}
                onClick={onEdit}
              >
                <EditIcon className={styles['edit-icon']} />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <CardComposeButton
          ref={content.type === TEXT_TYPE ? setRef : undefined}
          wrapRef={props.composeBtnRef}
          className={styles['compose-btn']}
          label={props.createPostLabel}
          onClick={onCreatePost}
        />
      </React.Fragment>
    )
  }

  return (
    <div className={`${styles.actions} ${props.className}`} data-testid="content-card-actions">
      {content.feed.id && props.isFavButtonVisible && (
        <FavButton
          isFavorite={props.isFavorite}
          count={props.favCount}
          hover={false}
          tooltipPlacement="top"
          className={`${styles.btn} ${styles.secondary}`}
          onFavorite={props.onAddFavorite}
          onUnfavorite={props.onRemoveFavorite}
        />
      )}
      <Tooltip
        title={props.createPostLabel || <FormattedMessage id="content-card.actions.create-post" />}
      >
        <IconButton
          size="small"
          className={`${styles.btn} ${!isCreatePostPrimary ? styles.secondary : ''}`}
          data-btn-toggle
          color={isCreatePostPrimary ? 'primary' : 'default'}
          ref={content.type === TEXT_TYPE ? setRef : undefined}
          onClick={onCreatePost}
        >
          {props.studioButtonIcon || <FeatherIcon className={styles['icon-post']} />}
        </IconButton>
      </Tooltip>
      {isPhoto && showStudioButton && (
        <Tooltip title={<FormattedMessage id="content-card.actions.edit-image" />}>
          <IconButton
            size="small"
            data-btn-toggle
            data-ui-dark
            className={`${styles.btn} ${styles.secondary}`}
            onClick={openStudio}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

export default memo(ContentCardActions)
